<template>
  <a-spin :spinning="loading">
    <section v-if="renewal" class="cna-renewal-widget">
      <div class="cna-renewal-header">
        <div>
          <div class="cna-renewal-title-row">
            <h4 class="cna-renewal-title">License Renewal Progress</h4>
            <a-tag class="m-0" :color="statusColor">{{ statusText }}</a-tag>
          </div>
          <p class="cna-renewal-subtitle">{{ onTrackText }}</p>
        </div>

        <a-button icon="download" size="small" :loading="downloadLoading" @click="downloadReport">
          Download PDF
        </a-button>
      </div>

      <div class="cna-renewal-summary">
        <div class="cna-renewal-main">
          <span class="cna-renewal-stat-label">Continuing education completed</span>
          <div class="cna-renewal-hours">
            <strong>{{ formatHoursCounter(summary.completed_hours, summary.target_hours) }}</strong>
          </div>
          <a-progress :percent="progressPercent" :status="progressStatus" :stroke-width="8" :show-info="false" />
          <div class="cna-renewal-progress-meta">
            <span>{{ progressPercent }}% of target</span>
            <span>{{ formatHours(summary.remaining_hours) }} remaining</span>
          </div>
        </div>

        <div class="cna-renewal-details">
          <div class="cna-renewal-detail">
            <span>License cycle</span>
            <strong>{{ licenseCycle }}</strong>
          </div>
          <div class="cna-renewal-detail">
            <span>Due date</span>
            <strong>{{ dueDate }}</strong>
          </div>
          <div class="cna-renewal-detail cna-renewal-detail--wide">
            <span>Delivery format</span>
            <strong>{{ formatHours(offlineHours) }} in-person - {{ formatHours(onlineHours) }} online</strong>
            <small>Online counts toward a maximum of 24 hrs</small>
          </div>
        </div>
      </div>

      <div class="cna-renewal-breakdowns">
        <div class="cna-renewal-section">
          <div class="cna-renewal-section-title">
            <span>Two-year breakdown</span>
            <small>24 hrs minimum each year</small>
          </div>

          <div class="cna-renewal-pill-list">
            <div v-for="year in yearBreakdown" :key="year.key" class="cna-renewal-pill">
              <div>
                <div class="cna-renewal-pill-label">
                  {{ year.label }}
                  <a-tag v-if="year.current" :color="'#2563eb'">Current</a-tag>
                </div>
                <span>{{ formatHoursCounter(year.hours, 24) }}</span>
                <span>{{ formatHours(year.online) }} online, {{ formatHours(year.offline) }} in-person</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="cna-renewal-section">
        <div class="cna-renewal-section-title">
          <span>Validation messages</span>
        </div>

        <div v-if="rules.length" class="cna-renewal-rules">
          <a-alert
            v-for="rule in rules"
            :key="rule.code"
            :type="getRuleAlertType(rule.level)"
            :message="rule.text"
            show-icon
          />
        </div>
        <a-empty v-else description="No issues found." />
      </div>
    </section>
  </a-spin>
</template>

<script>
import { Alert, Button, Empty, notification, Progress, Spin, Tag } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import moment from 'moment-timezone'
import util from '@/util'

const { mapActions: profileActions } = createNamespacedHelpers('profile')

export default {
  name: 'CnaRenewalHoursWidget',
  components: {
    'a-alert': Alert,
    'a-button': Button,
    'a-empty': Empty,
    'a-progress': Progress,
    'a-spin': Spin,
    'a-tag': Tag
  },
  props: {
    associate: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      downloadLoading: false,
      loading: false,
      renewal: undefined
    }
  },
  computed: {
    summary() {
      return {
        completed_hours: this.renewal?.completed_hours ?? 0,
        target_hours: this.renewal?.target_hours ?? 0,
        remaining_hours: this.renewal?.remaining_hours ?? 0,
        percentage: this.renewal?.percentage ?? 0
      }
    },
    rules() {
      return this.renewal?.rules ?? []
    },
    progressPercent() {
      return Math.min(Number(this.summary.percentage ?? 0), 100)
    },
    hasErrors() {
      return this.rules.some(rule => rule.level === 'error')
    },
    isAtRisk() {
      return this.rules.some(rule => rule.code === 'associate_target_not_reached_at_current_pace')
    },
    statusText() {
      if (this.hasErrors) return 'Action needed'

      return this.progressPercent >= 100 ? 'Ready' : 'In progress'
    },
    statusColor() {
      if (this.hasErrors) return '#ef4444'

      return this.progressPercent >= 100 ? '#10b981' : '#f59e0b'
    },
    progressStatus() {
      if (this.hasErrors) return 'exception'

      return this.progressPercent >= 100 ? 'success' : 'active'
    },
    dueDate() {
      return this.formatDate(this.associate?.license?.expiration_date)
    },
    licenseCycle() {
      const expirationDate = this.associate?.license?.expiration_date

      if (!expirationDate) return 'Current license cycle'

      const end = moment(expirationDate).utc()
      const start = end.clone().subtract(2, 'years')

      return `${start.format('MM/DD/YYYY')} - ${end.format('MM/DD/YYYY')}`
    },
    onTrackText() {
      return this.isAtRisk ? 'Not enough hours to renew by the deadline' : `On track to renew by ${this.dueDate}`
    },
    firstYearHours() {
      return Number(this.renewal?.first_year_online ?? 0) + Number(this.renewal?.first_year_offline ?? 0)
    },
    secondYearHours() {
      return Number(this.renewal?.second_year_online ?? 0) + Number(this.renewal?.second_year_offline ?? 0)
    },
    onlineHours() {
      return Number(this.renewal?.first_year_online ?? 0) + Number(this.renewal?.second_year_online ?? 0)
    },
    offlineHours() {
      return Number(this.renewal?.first_year_offline ?? 0) + Number(this.renewal?.second_year_offline ?? 0)
    },
    isSecondYearCurrent() {
      const expirationDate = this.associate?.license?.expiration_date

      if (!expirationDate || this.renewal?.first_year_current) return false

      const now = moment().utc()
      const end = moment(expirationDate).utc()
      const start = end.clone().subtract(1, 'years')

      return now.isSameOrAfter(start) && now.isBefore(end)
    },
    yearBreakdown() {
      return [
        {
          key: 'year_1',
          label: 'Year 1',
          hours: this.firstYearHours,
          online: Number(this.renewal?.first_year_online ?? 0),
          offline: Number(this.renewal?.first_year_offline ?? 0),
          current: Boolean(this.renewal?.first_year_current)
        },
        {
          key: 'year_2',
          label: 'Year 2',
          hours: this.secondYearHours,
          online: Number(this.renewal?.second_year_online ?? 0),
          offline: Number(this.renewal?.second_year_offline ?? 0),
          current: this.isSecondYearCurrent
        }
      ]
    }
  },
  watch: {
    'associate.id': {
      handler(newVal) {
        if (newVal) {
          this.loadRenewal()
        }
      },
      immediate: true
    }
  },
  methods: {
    ...profileActions(['getAssociateTrainingRenewal', 'downloadAssociateTrainingRenewal']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    formatHours(value) {
      const number = Number(value ?? 0)

      return `${Number.isInteger(number) ? number : number.toFixed(1)} hrs`
    },

    formatHoursCounter(current, target) {
      return `${this.formatPlainHours(current)} / ${this.formatPlainHours(target)} hrs`
    },

    formatPlainHours(value) {
      const number = Number(value ?? 0)

      return Number.isInteger(number) ? number : number.toFixed(1)
    },

    formatDate(date) {
      return date ? moment(date).utc().format('MM/DD/YYYY') : 'License expiration date'
    },

    getRuleAlertType(level) {
      const map = {
        error: 'error',
        warning: 'warning',
        info: 'info'
      }

      return map[level] || 'info'
    },

    async downloadReport() {
      this.downloadLoading = true

      try {
        const url = await this.downloadAssociateTrainingRenewal({
          associate_id: this.associate.id
        })

        util.downloadFile(url, 'Continuing Education Report Ready to Download')
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.downloadLoading = false
      }
    },

    async loadRenewal() {
      this.loading = true

      try {
        this.renewal = await this.getAssociateTrainingRenewal({
          associate_id: this.associate.id
        })
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.cna-renewal-widget {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 16px;
  color: var(--text-color);
  background: var(--bg-component);
  border: 1px solid var(--border-color-base);
  border-radius: 8px;
}

.cna-renewal-header,
.cna-renewal-title-row,
.cna-renewal-progress-meta,
.cna-renewal-section-title,
.cna-renewal-pill {
  display: flex;
  gap: 12px;
}

.cna-renewal-header,
.cna-renewal-progress-meta,
.cna-renewal-section-title,
.cna-renewal-pill {
  justify-content: space-between;
}

.cna-renewal-header,
.cna-renewal-title-row,
.cna-renewal-pill {
  align-items: center;
}

.cna-renewal-title {
  margin: 0;
  font-size: 16px;
}

.cna-renewal-subtitle,
.cna-renewal-stat-label,
.cna-renewal-progress-meta,
.cna-renewal-detail span,
.cna-renewal-detail small,
.cna-renewal-section-title small,
.cna-renewal-pill span {
  color: var(--text-color-secondary);
}

.cna-renewal-subtitle {
  margin: 4px 0 0;
}

.cna-renewal-summary {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
  align-items: start;
}

.cna-renewal-main {
  min-width: 0;
}

.cna-renewal-hours {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 8px;
}

.cna-renewal-hours strong {
  font-size: 30px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.cna-renewal-details {
  display: grid;
  gap: 10px;
  min-width: 0;
  padding: 12px;
  background: var(--bg-dropdown-selected);
  border: 1px solid var(--border-color-base);
  border-radius: 6px;
}

.cna-renewal-detail {
  display: grid;
  grid-template-columns: minmax(92px, 0.7fr) minmax(0, 1fr);
  column-gap: 12px;
  row-gap: 4px;
  align-items: baseline;
  min-width: 0;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--border-color-base);
}

.cna-renewal-detail:last-child {
  padding-bottom: 0;
  border-bottom: none;
}

.cna-renewal-detail strong {
  min-width: 0;
}

.cna-renewal-detail small {
  grid-column: 2;
}

.cna-renewal-breakdowns {
  min-width: 0;
}

.cna-renewal-section {
  min-width: 0;
}

.cna-renewal-section-title {
  align-items: baseline;
  margin-bottom: 8px;
  font-weight: 700;
}

.cna-renewal-pill-list,
.cna-renewal-rules {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cna-renewal-pill {
  width: 100%;
  padding: 10px 12px;
  color: var(--text-color);
  background: transparent;
  border: 1px solid var(--border-color-base);
  border-radius: 6px;
}

.cna-renewal-pill-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cna-renewal-pill-label > span {
  color: white;
}

.cna-renewal-pill > div {
  display: flex;
  flex-direction: column;
  min-width: 0;
  width: 100%;
}

.cna-renewal-widget ::v-deep .ant-progress-inner {
  background-color: var(--border-color-base);
}

.cna-renewal-widget ::v-deep .ant-btn {
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    transform 0.2s ease;
}

.cna-renewal-widget ::v-deep .ant-btn:hover,
.cna-renewal-widget ::v-deep .ant-btn:focus {
  background-color: rgba(255, 255, 255, 0.04);
  transform: translateY(-1px);
}

.cna-renewal-widget ::v-deep .ant-btn:active {
  transform: translateY(0);
}

@media screen and (max-width: 1024px) {
  .cna-renewal-summary {
    grid-template-columns: 1fr;
  }

  .cna-renewal-header {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media screen and (max-width: 640px) {
  .cna-renewal-header,
  .cna-renewal-progress-meta,
  .cna-renewal-pill {
    flex-direction: column;
    align-items: flex-start;
  }

  .cna-renewal-details {
    padding: 10px;
  }

  .cna-renewal-detail {
    grid-template-columns: 1fr;
  }

  .cna-renewal-detail small {
    grid-column: 1;
  }
}
</style>
